// src/components/projects/ProjectDetailPage.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  identityApiRef,
  discoveryApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Page,
  Header,
  Content,
  Progress,
  InfoCard,
  Table,
} from '@backstage/core-components';
import {
  Tabs,
  Tab,
  Box,
  Typography,
  makeStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Grid,
  Paper,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LockIcon from '@material-ui/icons/Lock';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import yaml from 'js-yaml';
import { Link } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  tabs: {
    marginBottom: theme.spacing(2),
  },
  accordion: {
    width: '100%',
  },
  detailsBox: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  simpleEntry: {
    margin: theme.spacing(1, 0),
  },
  accessDeniedPaper: {
    padding: theme.spacing(4),
    textAlign: 'center',
    maxWidth: '600px',
    margin: '0 auto',
    marginTop: theme.spacing(4),
  },
  lockIcon: {
    fontSize: 72,
    color: theme.palette.error.main,
    margin: theme.spacing(2),
  },
  accessDeniedTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.error.main,
    fontWeight: 'bold',
  },
  accessDeniedMessage: {
    marginBottom: theme.spacing(4),
  },
  backButton: {
    marginTop: theme.spacing(2),
  },
}));

function TabPanel(props: {
  children?: React.ReactNode;
  value: number;
  index: number;
}) {
  const { children, value, index } = props;
  return value === index ? <Box p={1}>{children}</Box> : null;
}

type KV = { key: string; value: string };
type TeamMember = { name: string; email: string };
type EnvConfig = Record<string, any>;

export const ProjectDetailPage: React.FC = () => {
  const { projectName } = useParams<{ projectName?: string }>();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const classes = useStyles();
  const navigate = useNavigate();

  if (!projectName) {
    return (
      <Page themeId="home">
        <Header title="Unknown Project" />
        <Content>
          <Typography color="error">
            Error: projectName route parameter is missing
          </Typography>
        </Content>
      </Page>
    );
  }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [generalRows, setGeneralRows] = useState<KV[]>([]);
  const [products, setProducts] = useState<string[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [environments, setEnvironments] = useState<
    Record<string, EnvConfig>
  >({});
  const [tab, setTab] = useState(0);
  const [componentRows, setComponentRows] = useState<KV[]>([]);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [productCosts, setProductCosts] = useState<Record<string, { total: number; loading: boolean }>>({});
  const [detailedCosts, setDetailedCosts] = useState<Record<string, any>>({});
  const [costsLoading, setCostsLoading] = useState(false);

  useEffect(() => {
    identityApi.getProfileInfo()
      .then(p => p.email && setEmail(p.email))
      .catch(console.error);
  }, [identityApi]);

  useEffect(() => {
    if (!email) return;
    
    const checkAccess = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();

        // Check if user has access to this project
        const accessResponse = await fetch(`${baseUrl}/ado/user/projects`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ username: email }),
        });

        if (!accessResponse.ok) {
          throw new Error(`Failed to check project access: ${accessResponse.status}`);
        }

        const { projects, projectDetails } = (await accessResponse.json()) as {
          projects?: string[];
          projectDetails?: Array<{ name: string; adoProject: string; adoOrg?: string }>;
        };
        const userHasAccess = Array.isArray(projects) && projects.includes(projectName);
        // The team lives in the project's own Azure DevOps org; omitted = backend default.
        const adoOrg = projectDetails?.find(p => p.name === projectName)?.adoOrg;
        setHasAccess(userHasAccess);

        // Only continue loading project data if user has access
        if (!userHasAccess) {
          setLoading(false);
          return;
        }

        // A) Fetch descriptors map for team_descriptor lookup
        const projectsResp = await fetch(`${baseUrl}/aws/params`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: `/backstage/projects` }),
        });
        if (!projectsResp.ok) {
          throw new Error(
            `Failed to load /backstage/projects: ${projectsResp.status}`
          );
        }
        const { value: rawProjects } =
          (await projectsResp.json()) as { value: string };
        const allProjects = yaml.load(rawProjects) as Record<string, any>;
        const teamDescriptor: string | undefined =
          allProjects[projectName]?.team_descriptor;

        // B) Fetch full project config for general/products/environments
        const projectResp = await fetch(`${baseUrl}/aws/params`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: `/backstage/project/${projectName}` }),
        });
        if (!projectResp.ok) {
          throw new Error(
            `Failed to load /backstage/project/${projectName}: ${projectResp.status}`
          );
        }
        const { value: rawProject } =
          (await projectResp.json()) as { value: string };
        const projectConfig = yaml.load(rawProject) as Record<string, any>;

        // General (exclude products, environments, team_descriptor, components)
        const general: KV[] = [];
        for (const [k, v] of Object.entries(projectConfig)) {
          if (
            k === 'products' ||
            k === 'environments' ||
            k === 'team_descriptor' ||
            k === 'components'
          ) {
            continue;
          }
          general.push({ key: k, value: String(v) });
        }
        setGeneralRows(general);

        // Products
        setProducts(
          Array.isArray(projectConfig.products)
            ? projectConfig.products
            : []
        );

        // Environments
        setEnvironments(
          projectConfig.environments &&
            typeof projectConfig.environments === 'object'
            ? (projectConfig.environments as Record<string, EnvConfig>)
            : {}
        );
        // Components
        const comps = projectConfig.components && typeof projectConfig.components === 'object'
          ? Object.entries(projectConfig.components).map(([key, val]) => ({ key, value: String(val) }))
          : [];
        setComponentRows(comps);

        // Team members fetch
        if (teamDescriptor) {
          const memResp = await fetch(
            `${baseUrl}/ado/team/members`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(adoOrg ? { teamDescriptor, org: adoOrg } : { teamDescriptor }),
            }
          );

          if (memResp.ok) {
            const { members: rawMembers } =
              (await memResp.json()) as {
                members: Array<{ name: string; email: string }>;
              };
            setMembers(rawMembers);
          } else {
            console.warn(
              '→ Team members fetch failed:',
              memResp.status,
              await memResp.text()
            );
          }
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [projectName, identityApi, discoveryApi, email]);

  // Fetch costs for all products when Products tab is selected
  useEffect(() => {
    if (tab !== 1 || products.length === 0) return;

    const fetchProductCosts = async () => {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      // Initialize loading state for all products
      const initialCosts: Record<string, { total: number; loading: boolean }> = {};
      products.forEach(p => {
        initialCosts[p] = { total: 0, loading: true };
      });
      setProductCosts(initialCosts);

      // Fetch costs for each product
      for (const product of products) {
        try {
          const resp = await fetch(
            `${baseUrl}/aws/product-costs?productName=${encodeURIComponent(product)}&projectName=${encodeURIComponent(projectName)}`,
            {
              method: 'GET',
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (resp.ok) {
            const data = await resp.json();
            
            // Aggregate costs across all environments
            let totalCost = 0;
            if (data.costs) {
              Object.values(data.costs).forEach((envCost: any) => {
                if (envCost.totalCost && !envCost.error) {
                  totalCost += parseFloat(envCost.totalCost);
                }
              });
            }

            setProductCosts(prev => ({
              ...prev,
              [product]: { total: totalCost, loading: false },
            }));
          } else {
            setProductCosts(prev => ({
              ...prev,
              [product]: { total: 0, loading: false },
            }));
          }
        } catch (e) {
          console.error(`Failed to fetch costs for ${product}:`, e);
          setProductCosts(prev => ({
            ...prev,
            [product]: { total: 0, loading: false },
          }));
        }
      }
    };

    fetchProductCosts();
  }, [tab, products, projectName, discoveryApi, identityApi]);

  // Fetch detailed costs for all products when Costs tab is selected
  useEffect(() => {
    if (tab !== 5 || products.length === 0) return;

    const fetchDetailedCosts = async () => {
      setCostsLoading(true);
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      const allCosts: Record<string, any> = {};

      for (const product of products) {
        try {
          const resp = await fetch(
            `${baseUrl}/aws/product-costs?productName=${encodeURIComponent(product)}&projectName=${encodeURIComponent(projectName)}`,
            {
              method: 'GET',
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (resp.ok) {
            const data = await resp.json();
            allCosts[product] = data.costs || {};
          } else {
            allCosts[product] = { error: 'Failed to load costs' };
          }
        } catch (e) {
          console.error(`Failed to fetch costs for ${product}:`, e);
          allCosts[product] = { error: 'Failed to load costs' };
        }
      }

      setDetailedCosts(allCosts);
      setCostsLoading(false);
    };

    fetchDetailedCosts();
  }, [tab, products, projectName, discoveryApi, identityApi]);

  if (loading) {
    return (
      <Page themeId="home">
        <Header title={projectName} />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (hasAccess === false) {
    return (
      <Page themeId="home">
        <Header title="Access Denied" />
        <Content>
          <Paper elevation={3} className={classes.accessDeniedPaper}>
            <Grid container direction="column" alignItems="center">
              <LockIcon className={classes.lockIcon} />
              <Typography variant="h4" className={classes.accessDeniedTitle}>
                Access Denied
              </Typography>
              <Typography variant="body1" className={classes.accessDeniedMessage}>
                You don't have permission to access the project <strong>{projectName}</strong>. 
                Please contact your administrator if you believe you should have access.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/projects')}
                className={classes.backButton}
              >
                Back to Projects
              </Button>
            </Grid>
          </Paper>
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page themeId="home">
        <Header title={projectName} />
        <Content>
          <Typography color="error">Error: {error}</Typography>
        </Content>
      </Page>
    );
  }

  const tableOptions = {
    paging: false,
    search: false,
    showTitle: false,
    toolbar: false,
  };

  return (
    <Page themeId="home">
      <Header title={projectName} />
      <Content>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          className={classes.tabs}
        >
          <Tab label="General" />
          <Tab label="Products" />
          <Tab label="Team" />
          <Tab label="Environments" />
          <Tab label="Components" />
          <Tab label="Costs" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <InfoCard>
            <Table<KV>
              options={tableOptions}
              columns={[{ title: 'Property', field: 'key' }, { title: 'Value', field: 'value' }]}
              data={generalRows}
            />
          </InfoCard>
        </TabPanel>

        <TabPanel value={tab} index={1}>
  <InfoCard>
    <Table<{ product: string; cost: string }>
      options={tableOptions}
      columns={[
        {
          title: 'Product',
          field: 'product',
          render: (row: { product: string }) => (
            <Link to={`/projects/${projectName}/${row.product}`}> 
              {row.product}
            </Link>
          ),
        },
        {
          title: 'Total Cost (30 days)',
          field: 'cost',
          render: (row: { product: string; cost: string }) => {
            const costData = productCosts[row.product];
            if (!costData) {
              return <span>-</span>;
            }
            if (costData.loading) {
              return <span style={{ color: '#666' }}>Loading...</span>;
            }
            if (costData.total === 0) {
              return <span>$0.00</span>;
            }
            return (
              <Link 
                to={`/projects/${projectName}/${row.product}?tab=costs`}
                style={{ 
                  color: '#1976d2', 
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
              >
                ${costData.total.toFixed(2)} USD
              </Link>
            );
          },
        },
      ]}
      data={products.map(p => ({ product: p, cost: '' }))}
    />
  </InfoCard>
</TabPanel>

        <TabPanel value={tab} index={2}>
          <InfoCard>
            <Table<TeamMember>
              options={tableOptions}
              columns={[{ title: 'Name', field: 'name' }, { title: 'Email', field: 'email' }]}
              data={members}
            />
          </InfoCard>
        </TabPanel>

        <TabPanel value={tab} index={3}>
  <div className={classes.accordion}>
    {Object.entries(environments).map(([envKey, cfg]) => {
      // separate simple and nested entries
      const entries = Object.entries(cfg).filter(
        ([k]) => k !== 'environment' && k !== 'allowed_aws_regions'
      );
      const simpleEntries = entries.filter(([, v]) => typeof v !== 'object' || v === null) as [string, any][];
      const nestedEntries = entries.filter(([, v]) => typeof v === 'object' && v !== null) as [string, any][];
      return (
        <Accordion key={envKey}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{envKey}</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.detailsBox}>
            {simpleEntries.length > 0 && (
              <Table<KV>
                options={tableOptions}
                columns={[{ title: 'Property', field: 'key' }, { title: 'Value', field: 'value' }]}
                data={simpleEntries.map(([k, v]) => ({ key: k, value: String(v) }))}
              />
            )}
            {nestedEntries.map(([key, value]) => (
              <Accordion key={key}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{key}</Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.detailsBox}>
                  <Table<KV>
                    options={tableOptions}
                    columns={[{ title: 'Property', field: 'key' }, { title: 'Value', field: 'value' }]}
                    data={Object.entries(value).map(
                      ([k, v]) => ({ key: k, value: String(v) })
                    )}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionDetails>
        </Accordion>
      );
    })}
  </div>
</TabPanel>
        <TabPanel value={tab} index={4}>
          <InfoCard>
            <Table<KV>
              options={tableOptions}
              columns={[{ title: 'Component', field: 'key' }, { title: 'Value', field: 'value' }]}
              data={componentRows}
            />
          </InfoCard>
        </TabPanel>

        <TabPanel value={tab} index={5}>
          {costsLoading ? (
            <Progress />
          ) : (
            <InfoCard>
              <Table<{ product: string; cost: string; change: string; changeValue: number }>
                options={tableOptions}
                columns={[
                  {
                    title: 'Product',
                    field: 'product',
                  },
                  {
                    title: 'Total Cost (30 days)',
                    field: 'cost',
                    render: (row: { product: string; cost: string }) => {
                      if (row.cost === 'Loading...') {
                        return <span style={{ color: '#666' }}>Loading...</span>;
                      }
                      if (row.cost === '$0.00 USD') {
                        return <span>$0.00 USD</span>;
                      }
                      return (
                        <Link 
                          to={`/projects/${projectName}/${row.product}?tab=costs`}
                          style={{ 
                            color: '#1976d2', 
                            fontWeight: 'bold',
                            textDecoration: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          {row.cost}
                        </Link>
                      );
                    },
                  },
                  {
                    title: 'Week-over-Week',
                    field: 'change',
                    render: (row: { change: string; changeValue: number }) => {
                      if (row.change === 'N/A') {
                        return <span style={{ color: '#666' }}>N/A</span>;
                      }
                      return (
                        <span style={{ 
                          color: row.changeValue >= 0 ? '#d32f2f' : '#388e3c',
                          fontWeight: 'bold'
                        }}>
                          {row.change}
                        </span>
                      );
                    },
                  },
                ]}
                data={Object.entries(detailedCosts).map(([productName, productCost]: [string, any]) => {
                  // Calculate total for this product and aggregate weekly costs
                  let productTotal = 0;
                  let totalCurrentWeek = 0;
                  let totalPreviousWeek = 0;
                  
                  if (productCost && !productCost.error) {
                    Object.values(productCost).forEach((envCost: any) => {
                      if (envCost.totalCost && !envCost.error) {
                        productTotal += parseFloat(envCost.totalCost);
                      }
                      
                      // Aggregate weekly costs across environments
                      if (envCost.weeklyCosts && envCost.weeklyCosts.length >= 2) {
                        const currentWeek = envCost.weeklyCosts[envCost.weeklyCosts.length - 1];
                        const previousWeek = envCost.weeklyCosts[envCost.weeklyCosts.length - 2];
                        totalCurrentWeek += parseFloat(currentWeek.cost);
                        totalPreviousWeek += parseFloat(previousWeek.cost);
                      }
                    });
                  }
                  
                  // Calculate aggregate percentage change
                  let changeDescription = 'N/A';
                  let changeValue = 0;
                  if (totalPreviousWeek > 0) {
                    changeValue = ((totalCurrentWeek - totalPreviousWeek) / totalPreviousWeek) * 100;
                    const sign = changeValue >= 0 ? '+' : '';
                    changeDescription = `${sign}${changeValue.toFixed(1)}%`;
                  }
                  
                  return {
                    product: productName,
                    cost: productTotal > 0 ? `$${productTotal.toFixed(2)} USD` : '$0.00 USD',
                    change: changeDescription,
                    changeValue: changeValue,
                  };
                })}
              />
              {Object.keys(detailedCosts).length === 0 && !costsLoading && (
                <Typography>No cost data available.</Typography>
              )}
            </InfoCard>
          )}
        </TabPanel>
      </Content>
    </Page>
  );
};
